import React, { useState, useRef } from 'react';
import { Upload, X, FileText, Check, Trash2, Loader2, AlertCircle } from 'lucide-react';
import apiService from '../../../../services/api';

const EvidenceUploader = ({ indicatorId, auditId, currentFiles = [], onUploadComplete, onDeleteComplete }) => {
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Reset
        setUploadError(null);
        setIsUploading(true);

        try {
            await apiService.sqaaf.uploadEvidence(indicatorId, file, auditId);
            if (onUploadComplete) onUploadComplete(); // Refresh parent
        } catch (error) {
            console.error("Upload failed", error);
            setUploadError("Failed to upload. Please try PDF/Images < 5MB.");
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleDelete = async (fileId) => {
        if (!window.confirm("Delete this evidence?")) return;
        try {
            await apiService.sqaaf.deleteEvidence(fileId);
            if (onDeleteComplete) onDeleteComplete();
        } catch (error) {
            console.error("Delete failed", error);
            alert("Could not delete file");
        }
    };

    return (
        <div className="mt-4 border-t border-gray-100 pt-4">
            <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-500" />
                Evidence Vault
                <span className="text-xs font-normal text-gray-400">(Recommended for strong claims)</span>
            </h5>

            {/* File List */}
            <div className="space-y-2 mb-4">
                {currentFiles.length === 0 && (
                    <div className="text-sm text-gray-400 italic">No evidence uploaded yet.</div>
                )}
                {currentFiles.map((file) => (
                    <div key={file.file_id || file.id} className="flex items-center justify-between bg-gray-50 p-2.5 rounded-lg border border-gray-200 group hover:border-blue-300 transition-colors">
                        <a
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 flex-1 overflow-hidden"
                        >
                            <div className="p-1.5 bg-white rounded border border-gray-200">
                                <FileText className="w-4 h-4 text-blue-600" />
                            </div>
                            <span className="text-sm text-gray-700 truncate hover:text-blue-600 hover:underline">
                                {file.filename}
                            </span>
                        </a>
                        <button
                            onClick={() => handleDelete(file.file_id)}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100"
                            title="Delete File"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                ))}
            </div>

            {/* Upload Area */}
            <div className="relative">
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                />
                <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className={`
                        w-full border-2 border-dashed border-gray-300 rounded-xl p-4
                        flex flex-col items-center justify-center gap-2
                        hover:border-blue-400 hover:bg-blue-50 transition-all group
                        ${isUploading ? 'opacity-70 cursor-wait' : ''}
                    `}
                >
                    {isUploading ? (
                        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                    ) : (
                        <>
                            <div className="p-2 bg-blue-100 text-blue-600 rounded-full group-hover:scale-110 transition-transform">
                                <Upload className="w-5 h-5" />
                            </div>
                            <span className="text-sm font-medium text-gray-600 group-hover:text-blue-700">
                                Click to Upload Evidence
                            </span>
                        </>
                    )}
                </button>
                {uploadError && (
                    <div className="mt-2 text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {uploadError}
                    </div>
                )}
            </div>
        </div>
    );
};

export default EvidenceUploader;
